import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';
import type { ContractSigning, EsignDocKind, EsignKindDef, GuaranteeBond, Uuid, VerificationChallenge } from '@/lib/types';

/**
 * 전자계약 서명 (V625 · DOC-98 §11) — `/api/external/sites/{siteId}/contract-signings/…`
 *
 * <p>순서: 계약서 보기 → 인증번호 받기(계정에 등록된 휴대폰으로 카카오 알림톡) → 맞히기 → 서명 그리기 → 서명.
 * 번호는 화면에서 받지 않는다 — 서버가 계정의 번호로만 보낸다.
 */

export const signingKeys = {
  list: (siteId: Uuid) => ['site', siteId, 'contract-signings'] as const,
  kinds: (siteId: Uuid) => ['site', siteId, 'contract-signings', 'kinds'] as const,
  bonds: (siteId: Uuid) => ['site', siteId, 'guarantee-bonds'] as const,
};

const base = (siteId: Uuid) => `/sites/${siteId}/contract-signings`;

export function useContractSignings(siteId: Uuid, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: signingKeys.list(siteId),
    queryFn: () => apiFetch<ContractSigning[]>(base(siteId)),
    enabled: options.enabled ?? true,
  });
}

/** 내가 서명할 차례인 것 — 배지와 홈 알림이 같은 규칙으로 센다. */
export function pendingSignings(rows: ContractSigning[]): ContractSigning[] {
  return rows.filter((row) => row.status === 'SENT');
}

/** 원본(`original`) · 서명본(`progress`, 본사 날인 대기) · 완성본(`signed`). `download` 면 파일로 받는 링크. */
export function fetchSigningFile(siteId: Uuid, signingId: Uuid, kind: 'original' | 'progress' | 'signed', download: boolean) {
  return apiFetch<{ url: string }>(`${base(siteId)}/${signingId}/files/${kind}?download=${download}`);
}

export function startSigningOtp(siteId: Uuid, signingId: Uuid) {
  return apiFetch<VerificationChallenge>(`${base(siteId)}/${signingId}/otp`, { method: 'POST' });
}

export function confirmSigningOtp(siteId: Uuid, signingId: Uuid, verificationId: Uuid, code: string) {
  return apiFetch<{ ticket: string }>(`${base(siteId)}/${signingId}/otp/confirm`, {
    method: 'POST',
    body: { verificationId, code },
  });
}

export function useSignContract(siteId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ signingId, ticket, signature, mark }: { signingId: Uuid; ticket: string; signature: string; mark: 'SIGN' | 'SEAL' }) =>
      apiFetch<ContractSigning>(`${base(siteId)}/${signingId}/sign`, {
        method: 'POST',
        body: { ticket, signature, position: null, mark },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: signingKeys.list(siteId) });
      // 서명되면 계약 줄이 「계약체결」 로 바뀐다 — 계약 카드도 다시 읽는다.
      void queryClient.invalidateQueries({ queryKey: ['site', siteId, 'subcontracts'] });
    },
  });
}

// ── 협력사가 쓰는 서류(V629) — 선급금 포기각서 · 보증이행완료확인서 · 기성 청구서 ────────────

export function useVendorDocKinds(siteId: Uuid) {
  return useQuery({
    queryKey: signingKeys.kinds(siteId),
    queryFn: () => apiFetch<EsignKindDef[]>(`${base(siteId)}/kinds`),
    staleTime: Infinity,
  });
}

export function fetchVendorDraft(siteId: Uuid, subcontractId: Uuid, kind: EsignDocKind, claimId: Uuid | null) {
  const claim = claimId ? `&claimId=${claimId}` : '';
  return apiFetch<Record<string, string>>(`${base(siteId)}/draft?subcontractId=${subcontractId}&kind=${kind}${claim}`);
}

export function useCreateVendorDoc(siteId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { kind: EsignDocKind; subcontractId: Uuid; claimId: Uuid | null; data: Record<string, string> }) =>
      apiFetch<ContractSigning>(base(siteId), { method: 'POST', body }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: signingKeys.list(siteId) }),
  });
}

export function useDiscardVendorDoc(siteId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (signingId: Uuid) => apiFetch<null>(`${base(siteId)}/${signingId}`, { method: 'DELETE' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: signingKeys.list(siteId) }),
  });
}

// ── 보증증권(V629) ───────────────────────────────────────────────────────────

export function useGuaranteeBonds(siteId: Uuid) {
  return useQuery({
    queryKey: signingKeys.bonds(siteId),
    queryFn: () => apiFetch<GuaranteeBond[]>(`/sites/${siteId}/guarantee-bonds`),
  });
}

export function useSubmitBond(siteId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (form: FormData) => apiFetch<GuaranteeBond>(`/sites/${siteId}/guarantee-bonds`, { method: 'POST', body: form }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: signingKeys.bonds(siteId) }),
  });
}

export function fetchBondFile(siteId: Uuid, bondId: Uuid, download: boolean) {
  return apiFetch<{ url: string }>(`/sites/${siteId}/guarantee-bonds/${bondId}/file?download=${download}`);
}

/** 링크를 새 탭으로 보거나, 보고 있던 탭 그대로 내려받는다. */
export function openLink(url: string, download: boolean) {
  if (!download) {
    window.open(url, '_blank', 'noopener');
    return;
  }
  const anchor = document.createElement('a');
  anchor.href = url;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

export const BOND_KIND_LABEL: Record<GuaranteeBond['kind'], string> = {
  CONTRACT: '계약이행',
  ADVANCE: '선급금',
  DEFECT: '하자보수',
  PAYMENT: '하도급대금 지급',
};
